import PageContainer from 'components/PageContainer';
import InvestmentTransferForm, {
  SubmittedCashBalance,
  SubmittedShareholding,
} from './InvestmentTransferForm';
import { Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import Summary from './Summary';
import { useHistory, useParams } from 'react-router-dom';
import Error from '../Common/Error';
import InvestorSearchRow from './InvestorSearchRow';
import SuccessMessage from './SuccessMessage';
import useInvestmentTransfer from 'hooks/data/investmentTransfer/useInvestmentTransfer';
import { useGetRole } from 'hooks/ui/useGetRole';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import { useFirm } from 'hooks/data/firm/useFirm';
import {
  CreateAddInvestmentTransferStep,
  createAddInvestmentTransferRoute,
} from 'adminConstants/routes';
import useLocalStorage from 'hooks/ui/useLocalStorage';
import PageContent from 'components/PageContent';
import Spacing from 'components/Spacing';
import { useDisclosure } from 'further-ui/hooks';
import NotificationDialog from './NotificationDialog';

const headingText = 'Investors: Transfer subscriptions';

type SavedData = {
  recipientId: string | null;
  investorId: string | null;
  summary: {
    shareholdings: Array<SubmittedShareholding>;
    cashBalances: Array<SubmittedCashBalance>;
  } | null;
  firmId: string | null;
};

const AddInvestmentTransfer = () => {
  const { isSuperAdmin, firmId: userFirmId } = useGetRole();
  const { firms } = useFirm({ params: {} });
  const { push } = useHistory();
  const { investorId, step = CreateAddInvestmentTransferStep.Form } =
    useParams<{
      investorId: string;
      step: CreateAddInvestmentTransferStep;
    }>();

  const notificationDisclosure = useDisclosure();

  const { getData, saveData, clear } = useLocalStorage<SavedData>(
    'investment-transfer',
    {
      defaultData: {
        recipientId: null,
        investorId: null,
        firmId: null,
        summary: null,
      },
      validate: (data) =>
        (!userFirmId || data.firmId === userFirmId) &&
        data.investorId === investorId,
    },
  );

  const initialData = useMemo(getData, []);

  const [firmId, setFirmId] = useState(initialData.firmId ?? userFirmId);
  const [recipientId, setRecipientId] = useState<string | null>(
    initialData.recipientId,
  );
  const [transferId, setTransferId] = useState<string>();

  const handleSharesTransferSubmit = (
    submittedShareholdings: Array<SubmittedShareholding>,
    submittedCashBalances: Array<SubmittedCashBalance>,
  ) => {
    saveData({
      summary: {
        shareholdings: submittedShareholdings,
        cashBalances: submittedCashBalances,
      },
      investorId,
      recipientId,
      firmId: firmId ?? null,
    });

    push(
      createAddInvestmentTransferRoute(
        investorId,
        CreateAddInvestmentTransferStep.Summary,
      ),
    );
  };

  const handleRecipientSelect = (recipientId?: string) => {
    setRecipientId(recipientId ?? null);
  };

  const handleChangeFirm = (selectedFirmId: string) => {
    setFirmId(selectedFirmId);
  };

  const handleInvestmentTransferCreated = (investmentTransfer: {
    _id: string;
  }) => {
    clear();
    setTransferId(investmentTransfer._id);
  };

  const { createInvestmentTransfer, isCreatingInvestmentTransfer } =
    useInvestmentTransfer({ onCreate: handleInvestmentTransferCreated });

  const { summary } = initialData;

  const handleSubmit = ({
    notifyRecipientInvestor,
    notifySourceInvestor,
  }: {
    notifyRecipientInvestor: boolean;
    notifySourceInvestor: boolean;
  }) => {
    if (!recipientId) return;

    createInvestmentTransfer({
      //@ts-expect-error
      firmId,
      recipientId,
      sourceInvestorId: investorId,
      shareholdings:
        summary?.shareholdings.map(({ id, investment, sharesToTransfer }) => ({
          shareholdingId: id ?? '',
          investmentId: investment?.id ?? '',
          sharesToTransfer,
        })) ?? [],
      cashBalances:
        summary?.cashBalances.map(({ balanceToTransfer, firm }) => ({
          balanceToTransfer,
          firmId: firm?._id,
        })) ?? [],
      notifyRecipientInvestor,
      notifySourceInvestor,
    });
  };

  if (!investorId) {
    return (
      <PageContainer heading={headingText}>
        <PageContent>
          <Error message="You have to select investor to create a transfer." />
        </PageContent>
      </PageContainer>
    );
  }

  if (transferId) {
    return (
      <PageContainer heading={headingText}>
        <SuccessMessage sourceInvestorId={investorId} transferId={transferId} />
      </PageContainer>
    );
  }

  if (step === CreateAddInvestmentTransferStep.Summary && summary) {
    return (
      <PageContainer heading={headingText}>
        <PageContent>
          <NotificationDialog
            submitting={isCreatingInvestmentTransfer}
            onSubmit={handleSubmit}
            disclosure={notificationDisclosure}
          />
          <Summary
            shareholdings={summary.shareholdings}
            cashBalances={summary.cashBalances}
            recipientId={recipientId ?? undefined}
            sourceInvestorId={investorId}
            onSubmit={notificationDisclosure.onOpen}
            isCreatingInvestmentTransfer={isCreatingInvestmentTransfer}
          />
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer heading={headingText}>
      <PageContent>
        <Spacing>
          <Typography variant="h2" color="textSecondary">
            1. Select firm and recipient investor account
          </Typography>
          {isSuperAdmin && (
            <AppSelectBox
              required
              data={firms?.data?.result ?? []}
              valueKey="_id"
              labelKey="firmName"
              label="Firm"
              value={firmId}
              variant="outlined"
              onChange={(event) =>
                handleChangeFirm(event.target.value as string)
              }
            />
          )}
          {firmId ? (
            <>
              <InvestorSearchRow
                recipientId={recipientId}
                handleRecipientSelect={handleRecipientSelect}
                firmId={firmId}
                sourceInvestorId={investorId}
              />
              {recipientId ? (
                <InvestmentTransferForm
                  defaultValues={summary}
                  firmId={firmId}
                  sourceInvestorId={investorId}
                  onSubmit={handleSharesTransferSubmit}
                />
              ) : null}
            </>
          ) : null}
        </Spacing>
      </PageContent>
    </PageContainer>
  );
};

export default AddInvestmentTransfer;
